import React, { Component } from 'react'
import { injectIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { Tooltip, OverlayTrigger  } from 'react-bootstrap'

import PanelRowValuesAdapter from './PanelRowValuesAdapter'

import API from 'utils/api/api'
import API_V1_URL from 'utils/api/urls_v1'
import ObjectTools from 'utils/objects/objectTools'
import DataTools from 'utils/dataTools'
import DateTools from 'utils/dates/dateTools'
import IndicatorTools from 'utils/indicators/indicatorTools'

import Loading from 'components/Loading'
import Error from 'components/Error'
import SparkLineChart from './SparkLineChart'
import messages from './messages'
import panelMessages from 'modules/Content/PanelSection/messages'


const rankData = ( data, rankOrdering ) => {
	let orderedData = data.sort( (a, b) => b.value - a.value );
	if ( rankOrdering !== 'descending' ) {
	  orderedData = orderedData.reverse();
	}
	return addRank( orderedData );
  };

const addRank = (data) => {
	return data.map(function (item, i) {
		if (i > 0) {
			//Get our previous list item
			var prevItem = data[i - 1];
			if (prevItem.value === item.value) {
				//Same score = same rank
				item.rank = prevItem.rank;
			} else {
				//Not the same score, give em the current iterated index + 1
				item.rank = i + 1;
			}
		} else {
			//First item takes the rank 1 spot
			item.rank = 1;
		}

		return item;
	});
}


class PanelRowValues extends Component {
	constructor(props) {
		super(props)

		this.state = {
			loading: true,
			error: false
		}

		const {
			intl: { formatNumber, formatMessage }
		} = props

		this.formatNumber = formatNumber
		this.formatMessage = formatMessage

		PanelRowValuesAdapter.importFormatter(this.formatMessage)
	}

	static getDerivedStateFromProps(nextProps, prevState) {
		if (nextProps !== prevState) {
			return nextProps
		}

		return null
	}

	componentDidMount() {
		this.getData();
		// this.getRankingChart();
	}

	shouldComponentUpdate(nextProps, nextState) {
		const shouldUpdate = !ObjectTools.compareObjects(this.props, nextProps) || !ObjectTools.compareObjects(this.state, nextState)

		return shouldUpdate
	}

	componentDidUpdate(prevProps) {
		if (prevProps !== this.props) {
			this.setState({ loading: true, error: false, regionsData: JSON.parse(this.props.regionsData) }, () => this.getData())
		}
	}

	getData = (
		{ indicator, selectedRegionId, comparative } = {
			indicator: this.props.indicatorData.id,
			selectedRegionId: this.props.selectedRegionId,
			comparative: this.props.comparative
		}
	) => {
		let promises = [];

		// get time series for all needed regions: reg, country, eu
		// maybe get all the series for benchmarkregions plus country and eu and work with all that?
		let countryId = this.props.topLevelRegions["ctry"];
		let euId = this.props.topLevelRegions["eu"];
		let geo = [
			selectedRegionId,
			countryId,
			euId
		]
		// const generalPromise = Promise.resolve(API.getQuery( API_V1_URL.getIndicatorData({ indicator, geo })))
		// promises.push(generalPromise);

		const regionsData = JSON.parse(this.props.regionsData);
		const benchmarkRegionIds = regionsData.map( o => o[ "id" ] );
		benchmarkRegionIds.forEach( (geo_id) => {
			if ( geo_id !== selectedRegionId ) {
				geo.push( geo_id );
			}
		} );

		// v1 hispeed
		const completePromise = Promise.resolve(API.getQuery( API_V1_URL.getIndicatorData({ indicator, geo })));
		const geo_key = "geo";
		// v0 facade
		// const completePromise = Promise.resolve(API.getQuery( API_V1_URL.getIndicatorDataV0Facade({ indicator, geo })));
		// const geo_key = "geoset";
		promises.push(completePromise);

		Promise.all(promises)
		.then( values => {
			let data = values[0].data.sort( (a, b) => {
				return DataTools.ascending( a["time"], b["time"] );
			} );

			// we should have all the data in data, let's wrangle
			let availableTimes = DataTools.uniquePropertyValues( data, "time" ).sort( DataTools.descending );

			// v0
			// selectedRegionId = String(selectedRegionId);
			// countryId = String(countryId);
			// euId = String(euId);

			let summary = {
				"last": {},
				"prev": null,
				"delta": null,
				"series": {},
				"ranking": null
			};
			summary["series"]["rg"] = data.filter( d => (d["value"] != null & d[geo_key] === selectedRegionId));
			summary["series"]["ct"] = data.filter( d => (d["value"] != null & d[geo_key] === countryId));
			summary["series"]["eu"] = data.filter( d => (d["value"] != null & d[geo_key] === euId));

			// v0
			// let rankingValues = PanelRowValuesAdapter.firstComparableSet( data, benchmarkRegionIds.map( String ), availableTimes, 0.8, "geoset" );
			// v1
			let rankingValues = PanelRowValuesAdapter.firstComparableSet( data, benchmarkRegionIds, availableTimes, 0.8, geo_key );
			if ( rankingValues !== null ) {
				let rankedData = rankData( rankingValues, this.props.indicatorData.rankOrdering );
				let regionRank = rankedData.filter( d => (d[geo_key] === selectedRegionId))[0];
				if ( regionRank !== undefined ) {
					summary["ranking"] = {};
					summary["ranking"]["rank"] = regionRank["rank"];
					summary["ranking"]["time"] = regionRank["time"];
					summary["ranking"]["time_display"] = regionRank["time_display"];
					summary["ranking"]["count"] = rankedData.length;
				}
			}

			// find common years
			let commonTimes = DataTools.intersection( ["rg", "ct", "eu"].map( series => DataTools.uniquePropertyValues( summary["series"][series], "time" ) ) );
			let lastCommonYear = DataTools.arrayAt( commonTimes, -1 );
			let prevCommonYear = DataTools.arrayAt( commonTimes, -2 );
			
			let seriesDateValue = (series, time) => {
				return DataTools.arrayAt( series.filter((s) => s["time"] === time), 0 );
			};

			summary["last"]["rg"] = seriesDateValue( summary["series"]["rg"], lastCommonYear );
			summary["last"]["ct"] = seriesDateValue( summary["series"]["ct"], lastCommonYear );
			summary["last"]["eu"] = seriesDateValue( summary["series"]["eu"], lastCommonYear );

			this.props.updateTime( summary["last"]["rg"].time_display, DataTools.arrayAt( summary["series"]["rg"], -1 ).time_display );
			
			if ( prevCommonYear != null ) {
				summary["prev"] = {};
				summary["prev"]["rg"] = seriesDateValue( summary["series"]["rg"], prevCommonYear );
				summary["prev"]["ct"] = seriesDateValue( summary["series"]["ct"], prevCommonYear );
				summary["prev"]["eu"] = seriesDateValue( summary["series"]["eu"], prevCommonYear );

				let deltaValue = (v0, v1) => {
					if (v0 && v0["value"] !== null && v1 && v1["value"] !== null) {
						return v0["value"] - v1["value"];
					}
					return null;
				};

				summary["delta"] = {};
				summary["delta"]["rg"] = deltaValue( summary["last"]["rg"], summary["prev"]["rg"] );
				summary["delta"]["ct"] = deltaValue( summary["last"]["ct"], summary["prev"]["ct"] );
				summary["delta"]["eu"] = deltaValue( summary["last"]["ct"], summary["prev"]["eu"] );
			}

			this.setState({ summary: summary, loading: false });
		})
		.catch(reason => {
			console.log( reason );
			this.setState({ error: true, loading: false })
		});
	}

	formatValue(v, minFractionDigits = 0 ) {
		return IndicatorTools.formatValue( v, this.props.indicatorData.unit, minFractionDigits );
	}

	renderIfAllOK = () => {
		let renderingComponent = null;
		let unit = this.props.indicatorData.unit;

		const arrow = (v) => {
			if (v == null ) {
				return "";
			} else if ( v > 0 ) {
				//return <React.Fragment><span>{"\u2191"}</span></React.Fragment>;
				return <React.Fragment><i className="fal fa-arrow-up"></i></React.Fragment>;
			} else if ( v < 0 ) {
				//return <React.Fragment><span>{"\u2193"}</span></React.Fragment>;
				return <React.Fragment><i className="fal fa-arrow-down"></i></React.Fragment>;
			} else {
				//return <React.Fragment><span>{"="}</span></React.Fragment>;
				return <React.Fragment><i className="fal fa-exchange"></i></React.Fragment>;
			}
		};

		const getSparkLineOptions = (data) => {
			const values = data["rg"].map( d => { 
				return {
					x: DateTools.getYearFromDateString(d["time"]),
					//x: +d["time_display"],
					y: d["value"]
				}
			} ).filter( p => p.x >= 2000 );
			// const values_eu = data["eu"].map( d => { 
			// 	return {
			// 		x: DateTools.getYearFromDateString(d["time"]),
			// 		//x: +d["time_display"],
			// 		y: d["value"]
			// 	}
			// } );
			return {
				series: [ {
					data: values,
					// color: "rgba( 255, 0, 0, 0.8 )"
				}
				/*, {
					data: values_eu,
					color: "rgba( 0, 0, 255, 0.8 )"
				}*/ ],

				tooltip: {
					headerFormat: `<span style="font-size:10px">{point.x}</span><br/>`,
					pointFormat: `<b>{point.y:.2f}</b> ${unit}`
				  }
			}
		};

		const getValueTooltip = (summary_data, area) => {
			return <Tooltip id="tooltip">
				<table className='tooltip-table'>
					<tbody>
					<tr>
						<td className='text-right'>
							{summary_data.last[area].time_display}
						</td>
						<td className='text-left'>
							<strong>{this.formatValue( summary_data.last[area].value, 1 )}</strong>
						</td>
					</tr>
					<tr>
						<td className='text-right'>
							{summary_data.prev[area].time_display}
						</td>
						<td className='text-left'>
							<strong>{this.formatValue( summary_data.prev[area].value, 1 )}</strong>
						</td>
					</tr>
					</tbody>
				</table>
			</Tooltip>;
		};

		if (this.state.error) {
			renderingComponent = <React.Fragment>
					<Error />
			</React.Fragment>;
		} else if (this.state.loading) {
			renderingComponent = <React.Fragment>
					<Loading />
			</React.Fragment>;
		} else {
			let formattedValue = {};
			let tooltip = {};
			Object.keys( this.state.summary.last ).forEach( area => {
				if ( this.state.summary.last[area] && this.state.summary.last[area].value ) {
					formattedValue[area] = this.formatValue( this.state.summary.last[area].value );
					tooltip[area] = getValueTooltip( this.state.summary, area );
				} else {
					formattedValue[area] = "n/a";
					tooltip[area] = <Tooltip id="tooltip"></Tooltip>;
				}
			} );

			let rankFragment = "n/a";
			if ( this.state.summary.ranking ) {
				rankFragment = <React.Fragment>
					<div>{this.state.summary.ranking.rank} <span className="rank-count">/ {this.state.summary.ranking.count}</span></div>
				</React.Fragment>;
				tooltip["rank"] = <Tooltip id="tooltip">{this.state.summary.ranking.time_display}</Tooltip>;
			} else {
				tooltip["rank"] = <Tooltip id="tooltip">{this.formatMessage(messages.noDataAvailable)}</Tooltip>;
			}

			const getValueAndDelta = (ctxt) => {
				let delta = null;

				if ( this.state.summary.delta ) {
					delta = arrow(this.state.summary.delta[ctxt]);
				}
				return <OverlayTrigger overlay={tooltip[ctxt]} placement="top">
					<Link to="#" className='value-tooltip'>
						{formattedValue[ctxt]} {delta}
					</Link>
				</OverlayTrigger>;
			};

			renderingComponent = <React.Fragment>
				<div className="row">
					<div className="col-lg-8 col-md-4 col-xs-6">
						<div className="row">
							<div className="col-md-6 col-xs-6 hidden-lg">
								{this.formatMessage(panelMessages.columnRegion)}
							</div>
							<div className="col-lg-3 col-md-6 col-xs-6 panel-slim-column text-right">
								{getValueAndDelta( "rg" )}
							</div>
							<div className="col-md-6 col-xs-6 hidden-lg">
							{this.formatMessage(panelMessages.columnCountry)}
							</div>
							<div className="col-lg-3 col-md-6 col-xs-6 panel-slim-column text-right panel-indicator-value-other">
								{getValueAndDelta( "ct" )}
							</div>
							<div className="col-md-6 col-xs-6 hidden-lg" dangerouslySetInnerHTML={{ __html: this.formatMessage(panelMessages.columnEU) }} />
							<div className="col-lg-3 col-md-6 col-xs-6 panel-slim-column text-right panel-indicator-value-other">
								{getValueAndDelta( "eu" )}
							</div>
							<div className="col-md-6 col-xs-6 hidden-lg">
								{this.formatMessage(panelMessages.columnRanking)}
							</div>
							<div className="col-lg-3 col-md-6 col-xs-6 panel-slim-column text-right">
								<OverlayTrigger overlay={tooltip.rank} placement="top">
									<Link to="#" className='value-tooltip'>
										{rankFragment}
									</Link>
								</OverlayTrigger>
							</div>
						</div>
					</div>
					<div className="col-lg-4 col-md-8 col-xs-6">
						<div className="row">
							<SparkLineChart options={getSparkLineOptions(this.state.summary.series)}></SparkLineChart>
						</div>
					</div>
				</div>
			</React.Fragment>
		}
		return renderingComponent
	}

	render() {
		return <React.Fragment>{this.renderIfAllOK()}</React.Fragment>
	}
}

export default injectIntl(PanelRowValues)



// WEBPACK FOOTER //
// ./src/modules/Charts/PanelRow/PanelRowValues/PanelRowValues.jsx